import { Link } from "react-router-dom";
import { Calendar, ChevronRight, Droplets, Fuel } from "lucide-react";
import type { FuelYearSummary } from "@/lib/fuel-analytics";
import { formatCurrency, formatFuelPrice, formatNumber } from "@/lib/utils";

type Props = {
  years: FuelYearSummary[];
  basePath: string;
};

export function FuelYearPicker({ years, basePath }: Props) {
  if (years.length === 0) {
    return (
      <p className="rounded-2xl bg-canvas p-6 text-center text-sm text-muted">
        No fuel logs recorded yet. Yearly spend will appear once users log top-ups.
      </p>
    );
  }

  return (
    <div className="stagger-children grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
      {years.map((entry) => (
        <Link
          key={entry.year}
          to={`${basePath}/${entry.year}`}
          className="group hover-raise rounded-3xl bg-canvas p-5 hover:bg-surface"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-surface text-foreground shadow-[var(--shadow-elevation)]">
                <Calendar className="h-5 w-5" />
              </span>
              <div>
                <p className="text-xl font-extrabold tracking-tight">{entry.year}</p>
                <p className="text-xs font-semibold text-muted">
                  {entry.isCurrentYear ? "Year to date" : "Full year"}
                </p>
              </div>
            </div>
            <ChevronRight className="h-4 w-4 shrink-0 -translate-x-1 text-muted opacity-0 transition-all duration-300 ease-[var(--ease-out-expo)] group-hover:translate-x-0 group-hover:opacity-100" />
          </div>

          <p className="mt-4 text-2xl font-extrabold tracking-tight">
            {formatCurrency(entry.overview.totalSpent)}
          </p>

          <div className="mt-3 flex flex-wrap gap-3 text-xs font-semibold text-muted">
            <span className="inline-flex items-center gap-1">
              <Fuel className="h-3.5 w-3.5" />
              {formatNumber(entry.overview.totalFillUps)} fill-ups
            </span>
            <span className="inline-flex items-center gap-1">
              <Droplets className="h-3.5 w-3.5" />
              {formatNumber(Math.round(entry.overview.totalLitres))} L
            </span>
            <span>{formatFuelPrice(entry.overview.avgPricePerLitre)}/L avg</span>
          </div>
        </Link>
      ))}
    </div>
  );
}

export function FuelYearTabs({
  years,
  activeYear,
  basePath,
}: {
  years: number[];
  activeYear: number;
  basePath: string;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {years.map((year) => {
        const active = year === activeYear;
        return (
          <Link
            key={year}
            to={`${basePath}/${year}`}
            className={
              active
                ? "inline-flex items-center rounded-full bg-foreground px-4 py-2 text-sm font-semibold text-surface"
                : "inline-flex items-center rounded-full bg-canvas px-4 py-2 text-sm font-semibold text-muted transition hover:bg-canvas-hover hover:text-foreground"
            }
          >
            {year}
          </Link>
        );
      })}
    </div>
  );
}
